/**
 * This file exposes the Forum Vini print preview as a separate template.
 */

import { Image, Link, View } from '@react-pdf/renderer';
import React from 'react';
import {
  Page,
  // Stencil,
  Text,
  Title,
} from '../../pdf-design-system';
import { PagesProvider } from '../../pdf-design-system/Base/PageLayout/Page';
import UserLocaleProvider from '../../Providers/UserLocaleProvider';
import ForumViniCatalog, {
  afterQuery,
  ExhibitorContent,
  GourmetIcon,
  PageBody,
  ServicesIcon,
  theme,
  useBleed,
  WineIcon,
} from './ForumViniCatalog';
import messages from './locale/messages';

/**
 * Instead of exporting all the components to another file, we export just this function and afterQuery.
 * @param {*} param0
 * @returns
 */
export default function ForumViniCatalogPrintPreview({
  data,
  locale,
  options = { bleed: 0 },
  ...rest
}) {
  const sections = afterQuery(data, rest);
  return (
    <PagesProvider>
      <UserLocaleProvider locale={locale} messages={messages}>
        {sections.map((section, index) => {
          const { registration } = section;
          return (
            <PrintPreview
              registration={registration}
              options={options}
              locale={locale}
              {...rest}
            />
          );
        })}
      </UserLocaleProvider>
    </PagesProvider>
  );
}

/**
 * The print preview page includes tips and tricks to help the exhibitor create a nicer page.
 * @param {*} param0
 * @returns
 */
function PrintPreview({ registration, options, locale, ...rest }) {
  const { withBleed } = useBleed(options);

  return (
    <Page
      size={{ width: withBleed(276, 2), height: withBleed(210, 2) }}
      style={[
        { paddingVertical: withBleed(0), paddingHorizontal: withBleed(0) },
      ]}
      theme={theme}
      {...rest}
    >
      <View style={{ display: 'flex', flexDirection: 'row' }}>
        <CatalogTipsAndTricks
          registration={registration}
          style={{ marginVertical: '1cm', width: '50%' }}
        />
        <ExhibitorContent
          registration={registration}
          locale={locale}
          options={options}
          style={{
            width: withBleed(148, 2),
            flexShrink: 0,
            borderLeft: '1px solid, #CCC',
          }}
        />
      </View>
    </Page>
  );
}

function CatalogTipsAndTricks({ registration, ...rest }) {
  return (
    <PageBody {...rest}>
      <Title style={{ color: theme.colors.primary }}>Tips & Tricks</Title>
      <Text>
        Dieser Leitfaden erklärt, wie Sie Ihre Katalogseite so attraktiv wie
        möglich gestalten können.
      </Text>
      <Title
        variant="small"
        style={{ marginTop: 10, color: theme.colors.primary }}
      >
        Profilbild
      </Title>
      <Text>
        Stellen Sie sicher, dass Ihr Profilbild ein Querformat hat.{' '}
        <Link
          style={{ color: theme.colors.primary }}
          src={`https://app.bottlebooks.me/dashboard/app/events/${registration.collection.collectionId}/exhibitors/${registration.companyId}/profile`}
        >
          Bild ändern.
        </Link>
      </Text>
      <Title
        variant="small"
        style={{ marginTop: 10, color: theme.colors.primary }}
      >
        Zeilenumbrüche entfernen
      </Title>
      <Text>
        <Link
          style={{ color: theme.colors.primary }}
          src={`https://app.bottlebooks.me/dashboard/app/events/${registration.collection.collectionId}/exhibitors/${registration.companyId}/profile`}
        >
          Entfernen Sie Zeilenumbrüche
        </Link>{' '}
        aus Ihrer Unternehmensbeschreibung, damit sie in den verfügbaren Platz
        passt, der zur Verfügung steht.
      </Text>
      <Title
        variant="small"
        style={{ marginTop: 10, color: theme.colors.primary }}
      >
        Flaschenbilder mit weißem Hintergrund
      </Title>
      <Text>
        Flaschenbilder sollten entweder vor einem weißen oder transparenten
        Hintergrund stehen.{' '}
        <Link
          style={{ color: theme.colors.primary }}
          src={`https://app.bottlebooks.me/dashboard/app/events/${registration.collection.collectionId}/exhibitors/${registration.companyId}/products`}
        >
          Aktualisieren Sie die Produktbilder.
        </Link>
      </Text>
      <Title style={{ marginTop: 30, color: theme.colors.primary }}>
        Besonders für die Forum Vini
      </Title>
      <Text>
        Für das Forum Vini gibt es ein paar Besonderheiten zu beachten.
      </Text>
      <Title
        variant="small"
        style={{ marginTop: 10, color: theme.colors.primary }}
      >
        Angebote am Stand
      </Title>
      <Text>
        Zeigen Sie die Angebote an Ihrem Stand, indem Sie die Fragen am Ende
        Ihres Profils{' '}
        <Link
          style={{ color: theme.colors.primary }}
          src={`https://app.bottlebooks.me/dashboard/app/events/${registration.collection.collectionId}/exhibitors/${registration.companyId}/profile`}
        >
          beantworten.
        </Link>
      </Text>
      <View style={{ display: 'flex', flexDirection: 'row' }}>
        <WineIcon style={{ width: 25, height: 25 }} />
        <GourmetIcon style={{ width: 25, height: 25 }} />
        <ServicesIcon style={{ width: 25, height: 25 }} />
      </View>
      <Title
        variant="small"
        style={{ marginTop: 10, color: theme.colors.primary }}
      >
        3 Produkte können in den Printkatalog aufgenommen werden
      </Title>
      <ForumViniHighlights registration={registration} />
    </PageBody>
  );
}

const productCountFirstPage = 3;

function ForumViniHighlights({ registration }) {
  const productHighlightCount = registration.registeredProducts.nodes?.filter(
    (product) => product.includeInCatalog.value
  )?.length;

  if (productHighlightCount < productCountFirstPage)
    return (
      <Text>
        Sie können {productCountFirstPage} Produkte auswählen, die in den
        gedruckten Katalog aufzunehmen. Sie haben {productHighlightCount}{' '}
        ausgewählt. Sie können {productCountFirstPage - productHighlightCount}{' '}
        weitere Produkte auswählen, oder so belassen, wie es ist.
      </Text>
    );
  if (productHighlightCount === productCountFirstPage)
    return (
      <Text>
        Sie haben {productCountFirstPage} Produkte ausgewählt, die in den
        Druckkatalog aufzunehmen. Vergewissern Sie sich, dass es sich um die von
        Ihnen gewünschten Produkte handelt.
      </Text>
    );
  if (productHighlightCount > productCountFirstPage)
    return (
      <React.Fragment>
        <Text>
          Sie haben {productHighlightCount} Produkte ausgewählt, die in den
          Druckkatalog aufzunehmen. Bitte{' '}
          <Link
            style={{ color: theme.colors.primary }}
            src={`https://app.bottlebooks.me/dashboard/app/events/${registration.collection.collectionId}/exhibitors/${registration.companyId}/products`}
          >
            entfernen
          </Link>{' '}
          Sie {productHighlightCount - productCountFirstPage} Produkt(e), indem
          Sie diese Frage als 'nein' beantworten. Sie finden sie am Ende des
          Formulars für jedes Produkt.
        </Text>
        <Image
          src="https://res.cloudinary.com/bottlebooks/image/upload/v1659035135/pdf-objects/Meininger/ProductHighlight.png"
          style={{ marginTop: 10 }}
        />
      </React.Fragment>
    );
  return null;
}

ForumViniCatalogPrintPreview.fragment = ForumViniCatalog.fragment;
